import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { rootUiActions } from 'app-components/flux/root-ui';

import type { ChartOfAccount } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import RefundForm from 'components/Forms/RefundForm';
import QButton from 'components/QButton';
import QIconButton from 'components/QIconButton';
import QIllustration from 'components/QIllustration';
import type { FormikProps } from 'formik';

export const DIALOG_TYPE_REFUND_EDIT = 'DIALOG_TYPE_REFUND_EDIT';

type FormValuesType = {
  payee: string;
  amount: string;
  postedOn: string;
  accountId: string;
  coa: ChartOfAccount;
  memo: string;
  attachment: [
    {
      clientId: string;
      name: string;
      id: string;
    },
  ];
};

function RefundEditDialog(props: {
  transaction?: TransactionProps | TransactionRecord;
  onClose?: (reason: string) => void;
  dialogId?: string; // set automatically for uncontrolled dialog
  open?: boolean; // set manually for controlled dialog
}) {
  const { transaction, onClose, dialogId, open } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const formikRef = useRef<null | FormikProps<FormValuesType>>();

  const onCloseOverride = useCallback(
    (_event: null | undefined, reason: string) => {
      onClose?.(reason);
      if (open === undefined && dialogId) {
        // uncontrolled version of dialog
        dispatch(rootUiActions.removeDialog(dialogId));
      }
    },
    [dispatch, dialogId, onClose, open],
  );

  const onDirty = useCallback((dirty: boolean) => setDisableSubmit(!dirty), [setDisableSubmit]);

  const transactionTemplate = useMemo(
    () => (transaction && 'toJS' in transaction ? transaction.toJS() : transaction) || {},
    [transaction],
  );

  return (
    <StdDialog
      onClose={onCloseOverride}
      onKeyDown={() => {}}
      sharedcomponentid={'DIALOG_TYPE_REFUND_EDIT'}
      size='sm'
      title={transaction?.id ? 'Edit Refund' : 'Track a Refund'}
      {...props}
    >
      <Dump obj={transaction} />
      <DialogContent>
        <Box display='flex' paddingRight={12}>
          <Box m={3}>
            <QIllustration name='refundWallet' size='sm' />
          </Box>
          <Box display='flex' flexDirection='column' minWidth={0}>
            {' '}
            {/* https://css-tricks.com/flexbox-truncated-text/ */}
            <Typography>Waiting for money back on a return you made? Track it here.</Typography>
            <RefundForm
              // @ts-expect-error not ts
              transaction={transactionTemplate}
              formikRef={formikRef}
              onClose={onCloseOverride}
              onDirty={onDirty}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {transaction?.id && (
          <QIconButton
            style={{
              position: 'absolute',
              left: 24,
              borderRadius: '50%',
              border: `1px solid ${theme.palette.primary.main}`,
            }}
            color='primary'
            id='delete-refund-tracking'
            aria-label='delete refund tracking'
            onClick={() => {
              dispatch(
                transactionsActions.deleteTransaction(
                  'set' in transaction
                    ? transaction.set('isDeleted', true)
                    : transactionsTypes.mkCashFlowTransaction({
                        ...transaction,
                        isDeleted: true,
                      }),
                  {
                    undo: { userMessage: 'Refund tracking deleted.' },
                  },
                ),
              );
              onCloseOverride(undefined, 'delete');
            }}
          >
            <DeleteOutlineIcon />
          </QIconButton>
        )}
        <QButton
          id='refund-track-submit-button'
          variant='contained'
          disabled={Boolean(disableSubmit)}
          onClick={() => {
            formikRef.current?.submitForm?.();
          }}
        >
          Submit
        </QButton>
      </DialogActions>
    </StdDialog>
  );
}

RefundEditDialog.whyDidYouRender = true;

export default RefundEditDialog;
